import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { SnackbarService } from '../services/snackbar/snackbar.service';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_STRENGTH_LABELS,
  assessPasswordStrength,
  scorePassword,
} from '../shared/password-policy';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: false,
})
export class ChangePasswordComponent {
  readonly passwordMinLength = PASSWORD_MIN_LENGTH;

  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  loading = false;

  constructor(
    private authService: AuthService,
    private snackbar: SnackbarService,
    private router: Router,
  ) {}

  get username(): string {
    return this.authService.getCurrentUser()?.username || '';
  }

  get forced(): boolean {
    return this.authService.mustChangePassword();
  }

  get passwordScore(): number {
    return scorePassword(this.newPassword);
  }

  get passwordScoreLabel(): string {
    return PASSWORD_STRENGTH_LABELS[this.passwordScore] || '';
  }

  get passwordPolicyMessage(): string | null {
    if (!this.newPassword) {
      return null;
    }
    const assessment = assessPasswordStrength(this.newPassword, this.username);
    return assessment.valid ? null : assessment.message || 'Contraseña no válida';
  }

  get mismatch(): boolean {
    return (
      this.confirmPassword.length > 0 &&
      this.newPassword !== this.confirmPassword
    );
  }

  async submit(): Promise<void> {
    if (this.loading) {
      return;
    }

    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.snackbar.warning('Rellena todos los campos');
      return;
    }

    const assessment = assessPasswordStrength(this.newPassword, this.username);
    if (!assessment.valid) {
      this.snackbar.warning(
        assessment.message || 'La contraseña no cumple la política',
      );
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.snackbar.warning('La confirmación no coincide');
      return;
    }

    if (this.newPassword === this.currentPassword) {
      this.snackbar.warning('La nueva contraseña debe ser distinta de la actual');
      return;
    }

    this.loading = true;

    try {
      await this.authService.changePassword(
        this.currentPassword,
        this.newPassword,
      );
      this.snackbar.success(
        'Contraseña actualizada. Vuelve a iniciar sesión.',
        { duration: 4000 },
      );
      this.router.navigate(['/login']);
    } catch (error: any) {
      const message =
        error?.error?.message ||
        (error?.status === 429
          ? 'Demasiados intentos. Espera unos minutos.'
          : 'No se pudo cambiar la contraseña');
      this.snackbar.error(message, { duration: 4000 });
    } finally {
      this.loading = false;
    }
  }

  async cancel(): Promise<void> {
    // Only offered when the change is voluntary. A forced change has no way out
    // other than completing it or logging out.
    await this.authService.logout();
    this.router.navigate(['/login']);
  }
}
